import React, { useEffect, useRef, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { formatDateTime } from '@onboarding-dashboard/shared';
import type {
  WorkspaceMember,
  UserRole,
  MemberAuditLog,
  MemberAuditAction,
} from '@onboarding-dashboard/shared';
import { useAuth } from '../../contexts/AuthContext.js';
import { adminService } from '../../services/admin.js';
import membersTemplate from './members.html?raw';
import './members.css';

const AUDIT_ACTION_LABELS: Record<MemberAuditAction, string> = {
  invitation_created: 'Invitation Sent',
  invitation_revoked: 'Invitation Revoked',
  role_changed: 'Role Changed',
  roles_changed: 'Access Changed',
  access_removed: 'Access Removed',
};

function formatAuditDetails(log: MemberAuditLog): string {
  const d = log.details;
  switch (log.action) {
    case 'invitation_created': {
      const roles = (d.roles as string[]) ?? [];
      const role = d.role as string | null;
      if (roles.length > 0) return `Invited with access: ${roles.join(', ')}`;
      return role ? `Invited as ${role}` : 'Invited (no role)';
    }
    case 'invitation_revoked': {
      const roles = (d.roles as string[]) ?? [];
      const role = d.role as string | null;
      if (roles.length > 0) return `Revoked (had: ${roles.join(', ')})`;
      return role ? `Revoked (was: ${role})` : 'Revoked';
    }
    case 'role_changed': {
      const from = (d.fromRole as string | null) ?? 'none';
      const to = (d.toRole as string | null) ?? 'none';
      return `${from} → ${to}`;
    }
    case 'roles_changed': {
      const from = ((d.fromRoles as string[]) ?? []).join(', ') || 'none';
      const to = ((d.toRoles as string[]) ?? []).join(', ') || 'none';
      return `${from} → ${to}`;
    }
    case 'access_removed':
      return (d.method as string) === 'hard_delete' ? 'Account deleted' : 'Account deactivated';
    default:
      return '';
  }
}

function buildAuditRow(log: MemberAuditLog): string {
  const date = new Date(log.createdAt);
  const dateMain = date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
  const dateTime = date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
  const label = AUDIT_ACTION_LABELS[log.action] ?? log.action;
  const details = formatAuditDetails(log);
  const searchText = [log.performedByName, log.performedByEmail, log.targetEmail]
    .join(' ')
    .toLowerCase();
  return `<tr data-search="${searchText}">
    <td class="activity-log__date">
      <span class="activity-log__date-main">${dateMain}</span>
      ${dateTime}
    </td>
    <td>
      <span class="activity-log__actor">${escapeHtmlStatic(log.performedByName)}</span>
      <span class="activity-log__actor-email">${escapeHtmlStatic(log.performedByEmail)}</span>
    </td>
    <td><span class="activity-log__action-badge activity-log__action-badge--${log.action}">${label}</span></td>
    <td>${escapeHtmlStatic(log.targetEmail)}</td>
    <td class="activity-log__details">${escapeHtmlStatic(details)}</td>
  </tr>`;
}

function escapeHtmlStatic(str: string): string {
  return str
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

const ROLES: { value: UserRole; label: string }[] = [
  { value: 'employee', label: 'Employee' },
  { value: 'manager', label: 'Manager' },
  { value: 'hr', label: 'HR' },
  { value: 'admin', label: 'Admin' },
];

interface RoleGroup {
  label: string;
  viewerRole: string;
  elevatedRole?: string;
  elevatedLabel?: string;
}

const ROLE_GROUPS: RoleGroup[] = [
  {
    label: 'Mission Control',
    viewerRole: 'mc-viewer',
    elevatedRole: 'mc-editor',
    elevatedLabel: 'Editor',
  },
  {
    label: 'Command Center',
    viewerRole: 'cc-viewer',
    elevatedRole: 'cc-editor',
    elevatedLabel: 'Editor',
  },
  { label: 'Insights', viewerRole: 'insights-viewer' },
  { label: 'Canopus Command', viewerRole: 'canopus-viewer' },
  {
    label: 'Brand Book',
    viewerRole: 'brand-viewer',
    elevatedRole: 'brand-editor',
    elevatedLabel: 'Editor',
  },
];

const MC_ROLE_OPTIONS = [
  { value: 'mc-admin', label: 'MC Admin' },
  ...ROLE_GROUPS.flatMap((g) => [
    { value: g.viewerRole, label: g.label },
    ...(g.elevatedRole ? [{ value: g.elevatedRole, label: `${g.label} ${g.elevatedLabel}` }] : []),
  ]),
];

export function MembersPage(): React.ReactElement {
  const containerRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const { user: currentUser, hasRole, refreshUser } = useAuth();

  const isAdmin = hasRole('admin');
  const isMcAdminOnly = hasRole('mc-admin') && !isAdmin;

  // Editors: can only manage viewer roles for their pages
  const editorViewerRoles: string[] = [];
  if (hasRole('mc-editor')) editorViewerRoles.push('mc-viewer');
  if (hasRole('cc-editor')) editorViewerRoles.push('cc-viewer');
  const isEditorOnly = editorViewerRoles.length > 0 && !isAdmin && !hasRole('mc-admin');
  const isLimitedRole = isMcAdminOnly || isEditorOnly;

  const [members, setMembers] = useState<WorkspaceMember[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  // ─── Audit Log Modal State ──────────────────────────────
  const [auditLogs, setAuditLogs] = useState<MemberAuditLog[]>([]);
  const [auditPage, setAuditPage] = useState(1);
  const [auditTotalPages, setAuditTotalPages] = useState(1);
  const [auditTotal, setAuditTotal] = useState(0);
  const [auditLoading, setAuditLoading] = useState(false);
  const [auditError, setAuditError] = useState<string | null>(null);
  const auditPageSize = 50;

  // ─── Helpers ───────────────────────────────────────────

  const showMessage = useCallback((type: 'error' | 'success', message: string) => {
    const container = containerRef.current;
    if (!container) return;
    const el = container.querySelector<HTMLDivElement>(`#members-${type}`);
    const msgEl = container.querySelector<HTMLSpanElement>(`#members-${type}-message`);
    if (el && msgEl) {
      msgEl.textContent = message;
      el.style.display = 'block';
      if (type === 'success') {
        setTimeout(() => {
          el.style.display = 'none';
        }, 4000);
      }
    }
    const other = type === 'error' ? 'success' : 'error';
    const otherEl = container.querySelector<HTMLDivElement>(`#members-${other}`);
    if (otherEl) otherEl.style.display = 'none';
  }, []);

  const clearMessages = useCallback(() => {
    const container = containerRef.current;
    if (!container) return;
    const errorEl = container.querySelector<HTMLDivElement>('#members-error');
    const successEl = container.querySelector<HTMLDivElement>('#members-success');
    if (errorEl) errorEl.style.display = 'none';
    if (successEl) successEl.style.display = 'none';
  }, []);

  // ─── Data Fetching ─────────────────────────────────────

  const loadMembers = useCallback(
    async (silent = false) => {
      if (!silent) setIsLoading(true);
      const result = await adminService.listMembers();
      if (result.success && result.data) {
        setMembers(result.data);
      } else {
        showMessage('error', result.error?.message ?? 'Failed to load workspace members');
      }
      if (!silent) setIsLoading(false);
    },
    [showMessage]
  );

  useEffect(() => {
    void loadMembers();
  }, [loadMembers]);

  // ─── Audit Log ─────────────────────────────────────────

  const loadAuditLogs = useCallback(
    async (page: number) => {
      setAuditLoading(true);
      setAuditError(null);
      try {
        const result = await adminService.listAuditLogs(page, auditPageSize);
        if (result.success && result.data) {
          setAuditLogs(result.data.items);
          setAuditTotalPages(result.data.totalPages);
          setAuditTotal(result.data.total);
          setAuditPage(page);
        } else {
          setAuditError(result.error?.message ?? 'Failed to load activity log');
        }
      } catch {
        setAuditError('Failed to load activity log');
      } finally {
        setAuditLoading(false);
      }
    },
    [auditPageSize]
  );

  // Sync audit modal DOM whenever its data changes
  useEffect(() => {
    const container = document.getElementById('activity-modal-overlay');
    if (!container || container.style.display === 'none') return;

    const loadingEl = document.getElementById('activity-log-loading');
    const errorEl = document.getElementById('activity-log-error');
    const tableWrapper = document.getElementById('activity-log-table-wrapper');
    const tbody = document.getElementById('activity-log-table-body');
    const emptyEl = document.getElementById('activity-log-empty');
    const pagination = document.getElementById('activity-log-pagination');
    const prevBtn = document.getElementById('activity-log-prev-btn') as HTMLButtonElement | null;
    const nextBtn = document.getElementById('activity-log-next-btn') as HTMLButtonElement | null;
    const pageInfo = document.getElementById('activity-log-page-info');

    if (loadingEl) loadingEl.style.display = auditLoading ? '' : 'none';
    if (errorEl) {
      errorEl.style.display = auditError ? '' : 'none';
      errorEl.textContent = auditError ?? '';
    }
    if (tableWrapper)
      tableWrapper.style.display = !auditLoading && auditLogs.length > 0 ? '' : 'none';
    if (emptyEl)
      emptyEl.style.display = !auditLoading && !auditError && auditLogs.length === 0 ? '' : 'none';

    if (tbody && !auditLoading) {
      tbody.innerHTML = auditLogs.map(buildAuditRow).join('');
    }

    if (pagination && pageInfo && prevBtn && nextBtn) {
      pagination.style.display = auditTotalPages > 1 ? '' : 'none';
      pageInfo.textContent = `Page ${auditPage} of ${auditTotalPages} (${auditTotal} entries)`;
      prevBtn.disabled = auditPage <= 1;
      nextBtn.disabled = auditPage >= auditTotalPages;
    }
  }, [auditLogs, auditLoading, auditError, auditPage, auditTotalPages, auditTotal]);

  // ─── Render Tables ─────────────────────────────────────

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const tbody = container.querySelector<HTMLTableSectionElement>('#members-table-body');
    const loading = container.querySelector<HTMLDivElement>('#members-loading');
    const empty = container.querySelector<HTMLDivElement>('#members-empty');
    const table = container.querySelector<HTMLTableElement>('#members-table');
    const mainSection = table?.closest('.members-section') as HTMLElement | null;

    const externalSection = container.querySelector<HTMLElement>('#external-members-section');
    const externalTable = container.querySelector<HTMLTableElement>('#external-members-table');
    const externalTbody = container.querySelector<HTMLTableSectionElement>(
      '#external-members-table-body'
    );
    const externalEmpty = container.querySelector<HTMLDivElement>('#external-members-empty');

    if (isLoading) {
      if (loading) loading.style.display = '';
      if (empty) empty.style.display = 'none';
      if (table) table.style.display = 'none';
      if (externalSection) externalSection.style.display = 'none';
      return;
    }

    if (loading) loading.style.display = 'none';

    // Split members into internal (workspace) and external (non-workspace)
    const internalMembers = members.filter((m) => m.isWorkspaceUser);
    const externalMembers = members.filter((m) => !m.isWorkspaceUser);

    // ─── Internal members table ─────────────────────────────
    if (mainSection) mainSection.style.display = '';

    // Hide the "Role" column header for limited-role users (mc-admin-only / editor-only)
    const roleHeader = table?.querySelector<HTMLTableCellElement>('thead th:nth-child(5)');
    if (roleHeader) roleHeader.style.display = isLimitedRole ? 'none' : '';

    if (internalMembers.length === 0) {
      if (empty) empty.style.display = '';
      if (table) table.style.display = 'none';
    } else {
      if (empty) empty.style.display = 'none';
      if (table) table.style.display = '';
    }

    if (tbody) {
      const buildMcCheckboxes = (userId: string, currentRoles: string[]): string => {
        // Editors: only show viewer toggles for their pages
        if (isEditorOnly) {
          return ROLE_GROUPS.filter((g) => editorViewerRoles.includes(g.viewerRole))
            .map((g) => {
              const viewerChecked = currentRoles.includes(g.viewerRole);
              return `<div class="role-group" data-viewer-role="${g.viewerRole}">
                <label class="role-group__viewer"><input type="checkbox" class="mc-role-cb" data-user-id="${userId}" value="${g.viewerRole}" ${viewerChecked ? 'checked' : ''} /> ${g.label}</label>
              </div>`;
            })
            .join('');
        }

        const hasMcAdmin = currentRoles.includes('mc-admin');
        const isSelf = userId === currentUser?.id;
        // Self-demotion guard (belt-and-suspenders — backend enforces the same rule):
        // when rendering the viewer's own row, disable the mc-admin checkbox if they
        // currently hold it, so a single misclick can't lock them out of managing others.
        const selfDemoteBlock =
          isSelf && hasMcAdmin ? ' disabled title="You cannot remove your own MC Admin role"' : '';

        // mc-admin as standalone top-level toggle
        const adminHtml = `<div class="role-group" data-viewer-role="mc-admin">
          <label class="role-group__viewer"><input type="checkbox" class="mc-role-cb" data-user-id="${userId}" value="mc-admin" ${hasMcAdmin ? 'checked' : ''}${selfDemoteBlock} /> MC Admin</label>
        </div>`;

        const groupsHtml = ROLE_GROUPS.map((g) => {
          const viewerChecked = currentRoles.includes(g.viewerRole);

          let elevatedHtml = '';
          if (g.elevatedRole) {
            const elevatedChecked = currentRoles.includes(g.elevatedRole) ? 'checked' : '';
            const showElevated = viewerChecked ? '' : 'style="display: none;"';
            elevatedHtml = `<div class="role-group__elevated" ${showElevated}>
              <label class="role-group__elevated-label"><input type="checkbox" class="mc-role-cb" data-user-id="${userId}" value="${g.elevatedRole}" ${elevatedChecked} /> └ ${g.elevatedLabel}</label>
            </div>`;
          }

          return `<div class="role-group" data-viewer-role="${g.viewerRole}">
            <label class="role-group__viewer"><input type="checkbox" class="mc-role-cb" data-user-id="${userId}" value="${g.viewerRole}" ${viewerChecked ? 'checked' : ''} /> ${g.label}</label>
            ${elevatedHtml}
          </div>`;
        }).join('');

        return adminHtml + groupsHtml;
      };

      tbody.innerHTML = internalMembers
        .map((m) => {
          const isSelf = currentUser?.email?.toLowerCase() === m.email.toLowerCase();
          const avatar = m.thumbnailPhotoUrl
            ? `<img class="member-avatar" src="${escapeHtml(m.thumbnailPhotoUrl)}" alt="" />`
            : `<span class="member-avatar-placeholder">${escapeHtml(getInitials(m.firstName, m.lastName))}</span>`;

          const { badgeClass, badgeText } = getAccessBadge(m);

          let roleCell = '';
          let mcRoleCell = '<span style="opacity:0.5">—</span>';
          let actionsCell = '';

          if (m.suspended) {
            roleCell = '<span style="opacity:0.5">—</span>';
            actionsCell = '';
          } else if (m.dashboardUserId && m.dashboardStatus === 'active') {
            const currentRole = m.dashboardRole ?? '';
            const noneSelected = !currentRole || currentRole === 'external' ? 'selected' : '';
            const noneOption = `<option value="" ${noneSelected}>None</option>`;
            const selectedRoleOptions = ROLES.map(
              (r) =>
                `<option value="${r.value}" ${r.value === currentRole ? 'selected' : ''}>${r.label}</option>`
            ).join('');
            const disableRole = isSelf || isLimitedRole;
            roleCell = `<select class="role-select" data-email="${escapeHtml(m.email)}" data-user-id="${m.dashboardUserId}" ${disableRole ? 'disabled' : ''}>${noneOption}${selectedRoleOptions}</select>`;
            mcRoleCell = `<div class="mc-role-checkboxes">${buildMcCheckboxes(m.dashboardUserId, m.dashboardRoles ?? [])}</div>`;
            actionsCell =
              isSelf || isLimitedRole
                ? ''
                : `<button class="btn-remove" data-user-id="${m.dashboardUserId}">Remove Access</button>`;
          } else if (m.pendingInvitationId) {
            const roleLabel =
              ROLES.find((r) => r.value === m.pendingInvitationRole)?.label ??
              m.pendingInvitationRole;
            roleCell = `<span style="opacity:0.6">${escapeHtml(roleLabel ?? '')}</span>`;
            actionsCell = isLimitedRole
              ? ''
              : `<button class="btn-revoke" data-invitation-id="${m.pendingInvitationId}">Revoke Invite</button>`;
          } else if (m.dashboardUserId && m.dashboardStatus === 'inactive') {
            roleCell = '<span style="opacity:0.5">—</span>';
            actionsCell = `<button class="btn-invite" data-email="${escapeHtml(m.email)}">Invite</button>`;
          } else {
            roleCell = '<span style="opacity:0.5">—</span>';
            actionsCell = `<button class="btn-invite" data-email="${escapeHtml(m.email)}">Invite</button>`;
          }

          const lastLogin = m.lastLoginAt ? formatDateTime(m.lastLoginAt) : '—';

          const roleTd = isLimitedRole ? '' : `<td>${roleCell}</td>`;

          return `<tr>
            <td>${avatar}</td>
            <td>${escapeHtml(m.fullName || `${m.firstName} ${m.lastName}`)}</td>
            <td>${escapeHtml(m.email)}</td>
            <td><span class="status-badge status-badge--${badgeClass}">${badgeText}</span></td>
            ${roleTd}
            <td>${mcRoleCell}</td>
            <td>${lastLogin}</td>
            <td><div class="action-group">${actionsCell}</div></td>
          </tr>`;
        })
        .join('');
    }

    // ─── External members table ─────────────────────────────
    if (externalSection) externalSection.style.display = '';

    // Hide the "Role" column header for mc-admin-only users in external table too
    const extRoleHeader =
      externalTable?.querySelector<HTMLTableCellElement>('thead th:nth-child(5)');
    if (extRoleHeader) extRoleHeader.style.display = isLimitedRole ? 'none' : '';

    if (externalMembers.length === 0) {
      if (externalTable) externalTable.style.display = 'none';
      if (externalEmpty) externalEmpty.style.display = '';
    } else {
      if (externalTable) externalTable.style.display = '';
      if (externalEmpty) externalEmpty.style.display = 'none';
    }

    if (externalTbody) {
      const buildExtMcCheckboxes = (userId: string, currentRoles: string[]): string => {
        // External users only get viewer roles — no editor or admin toggles
        const groups = isEditorOnly
          ? ROLE_GROUPS.filter((g) => editorViewerRoles.includes(g.viewerRole))
          : ROLE_GROUPS;

        return groups
          .map((g) => {
            const viewerChecked = currentRoles.includes(g.viewerRole);
            return `<div class="role-group" data-viewer-role="${g.viewerRole}">
            <label class="role-group__viewer"><input type="checkbox" class="mc-role-cb" data-user-id="${userId}" value="${g.viewerRole}" ${viewerChecked ? 'checked' : ''} /> ${g.label}</label>
          </div>`;
          })
          .join('');
      };

      externalTbody.innerHTML = externalMembers
        .map((m) => {
          const isSelf = currentUser?.email?.toLowerCase() === m.email.toLowerCase();
          const avatar = m.thumbnailPhotoUrl
            ? `<img class="member-avatar" src="${escapeHtml(m.thumbnailPhotoUrl)}" alt="" />`
            : `<span class="member-avatar-placeholder">${escapeHtml(getInitials(m.firstName, m.lastName))}</span>`;

          const { badgeClass, badgeText } = getAccessBadge(m);

          let mcRoleCell = '<span style="opacity:0.5">—</span>';
          let actionsCell = '';

          if (m.dashboardUserId && m.dashboardStatus === 'active') {
            mcRoleCell = `<div class="mc-role-checkboxes">${buildExtMcCheckboxes(m.dashboardUserId, m.dashboardRoles ?? [])}</div>`;
            actionsCell =
              isSelf || isLimitedRole
                ? ''
                : `<button class="btn-remove" data-user-id="${m.dashboardUserId}">Remove Access</button>`;
          } else if (m.dashboardUserId && m.dashboardStatus === 'inactive') {
            actionsCell = isLimitedRole
              ? ''
              : `<button class="btn-remove" data-user-id="${m.dashboardUserId}">Delete</button>`;
          } else if (m.pendingInvitationId) {
            const mcLabel = (m.pendingInvitationRoles ?? [])
              .map((r) => MC_ROLE_OPTIONS.find((o) => o.value === r)?.label ?? r)
              .join(', ');
            mcRoleCell = `<span style="opacity:0.6">${escapeHtml(mcLabel || 'MC')}</span>`;
            actionsCell = isLimitedRole
              ? ''
              : `<button class="btn-revoke" data-invitation-id="${m.pendingInvitationId}">Revoke Invite</button>`;
          }

          const lastLogin = m.lastLoginAt ? formatDateTime(m.lastLoginAt) : '—';
          const extRoleTd = isLimitedRole
            ? ''
            : '<td><span style="opacity:0.5">MC Only</span></td>';

          return `<tr>
            <td>${avatar}</td>
            <td>${escapeHtml(m.fullName || `${m.firstName} ${m.lastName}`)}</td>
            <td>${escapeHtml(m.email)}</td>
            <td><span class="status-badge status-badge--${badgeClass}">${badgeText}</span></td>
            ${extRoleTd}
            <td>${mcRoleCell}</td>
            <td>${lastLogin}</td>
            <td><div class="action-group">${actionsCell}</div></td>
          </tr>`;
        })
        .join('');
    }
  }, [
    members,
    isLoading,
    currentUser,
    isMcAdminOnly,
    isEditorOnly,
    isLimitedRole,
    editorViewerRoles,
  ]);

  // ─── Event Handlers ────────────────────────────────────

  const handleRoleChange = useCallback(
    async (userId: string, newRole: UserRole) => {
      clearMessages();
      const result = await adminService.updateUserRole(userId, newRole);
      if (result.success) {
        showMessage('success', 'Role updated successfully');
        void loadMembers(true);
      } else {
        showMessage('error', result.error?.message ?? 'Failed to update role');
        void loadMembers(true);
      }
    },
    [clearMessages, showMessage, loadMembers]
  );

  const handleMcRoleChange = useCallback(
    async (userId: string, roles: string[]) => {
      clearMessages();
      // Optimistically update local state so toggles reflect immediately
      setMembers((prev) =>
        prev.map((m) => (m.dashboardUserId === userId ? { ...m, dashboardRoles: roles } : m))
      );
      const result = await adminService.updateUserRoles(userId, roles);
      if (result.success) {
        showMessage('success', 'Analytics access updated');
        void refreshUser();
      } else {
        showMessage('error', result.error?.message ?? 'Failed to update analytics access');
        void loadMembers(true);
      }
    },
    [clearMessages, showMessage, loadMembers, refreshUser]
  );

  const handleInvite = useCallback(
    async (email: string, role: UserRole | null, roles?: string[]) => {
      clearMessages();
      const result = await adminService.createInvitation({
        email,
        role: role ?? undefined,
        roles,
      });
      if (result.success) {
        showMessage('success', `Invitation sent to ${email}`);
        void loadMembers(true);
      } else {
        showMessage('error', result.error?.message ?? 'Failed to send invitation');
      }
    },
    [clearMessages, showMessage, loadMembers]
  );

  const handleRevoke = useCallback(
    async (invitationId: string) => {
      clearMessages();
      const result = await adminService.revokeInvitation(invitationId);
      if (result.success) {
        showMessage('success', 'Invitation revoked');
        void loadMembers(true);
      } else {
        showMessage('error', result.error?.message ?? 'Failed to revoke invitation');
      }
    },
    [clearMessages, showMessage, loadMembers]
  );

  const handleRemoveAccess = useCallback(
    async (userId: string) => {
      clearMessages();
      const result = await adminService.removeUserAccess(userId);
      if (result.success) {
        showMessage('success', 'Access removed');
        void loadMembers(true);
      } else {
        showMessage('error', result.error?.message ?? 'Failed to remove access');
      }
    },
    [clearMessages, showMessage, loadMembers]
  );

  // ─── Attach Event Listeners ────────────────────────────

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const tbody = container.querySelector<HTMLTableSectionElement>('#members-table-body');
    const externalTbody = container.querySelector<HTMLTableSectionElement>(
      '#external-members-table-body'
    );

    // Role change via event delegation
    const handleChange = (e: Event): void => {
      const target = e.target as HTMLInputElement | HTMLSelectElement;
      if (target.classList.contains('role-select')) {
        const userId = target.dataset.userId;
        const value = (target as HTMLSelectElement).value;
        if (userId) {
          void handleRoleChange(userId, (value || null) as UserRole);
        }
      }
      if (target.classList.contains('mc-role-cb')) {
        const cb = target as HTMLInputElement;
        const userId = cb.dataset.userId;
        if (!userId) return;
        const mcContainer = cb.closest('.mc-role-checkboxes');
        const roleGroup = cb.closest('.role-group');
        const isViewerToggle = roleGroup?.querySelector('.role-group__viewer input') === cb;

        if (isViewerToggle) {
          // Viewer toggle changed
          const elevatedDiv = roleGroup?.querySelector<HTMLDivElement>('.role-group__elevated');
          const elevatedCb = elevatedDiv?.querySelector<HTMLInputElement>('input[type="checkbox"]');
          if (cb.checked) {
            // Show elevated sub-toggle
            if (elevatedDiv) elevatedDiv.style.display = '';
          } else {
            // Hide elevated, uncheck it
            if (elevatedDiv) elevatedDiv.style.display = 'none';
            if (elevatedCb) elevatedCb.checked = false;
          }
        }

        // Collect all checked roles and send
        const allChecked = Array.from(
          mcContainer?.querySelectorAll<HTMLInputElement>('.mc-role-cb:checked') ?? []
        ).map((c) => c.value);
        void handleMcRoleChange(userId, allChecked);
      }
    };
    tbody?.addEventListener('change', handleChange);

    // Button clicks via event delegation
    const handleClick = (e: Event): void => {
      const target = e.target as HTMLElement;

      if (target.classList.contains('btn-invite')) {
        const email = target.dataset.email;
        if (!email) return;
        // Replace the row with inline checkbox invite form
        const tr = target.closest('tr');
        if (tr) {
          // Capture avatar and name from existing cells
          const cells = tr.querySelectorAll('td');
          const avatarHtml = cells[0]?.innerHTML ?? '';
          const nameHtml = cells[1]?.innerHTML ?? '';
          const emailHtml = cells[2]?.innerHTML ?? '';

          let mcChecks: string;
          if (isEditorOnly) {
            // Editors: only viewer roles for their pages
            mcChecks = ROLE_GROUPS.filter((g) => editorViewerRoles.includes(g.viewerRole))
              .map(
                (g) => `<div class="role-group" data-viewer-role="${g.viewerRole}">
                <label class="role-group__viewer"><input type="checkbox" name="invite-mc" value="${g.viewerRole}"> ${g.label}</label>
              </div>`
              )
              .join('');
          } else {
            const adminCheck = `<div class="role-group" data-viewer-role="mc-admin">
              <label class="role-group__viewer"><input type="checkbox" name="invite-mc" value="mc-admin"> MC Admin</label>
            </div>`;
            const groupChecks = ROLE_GROUPS.map((g) => {
              let elevated = '';
              if (g.elevatedRole) {
                elevated = `<div class="role-group__elevated" style="display: none;">
                  <label class="role-group__elevated-label"><input type="checkbox" name="invite-mc" value="${g.elevatedRole}"> └ ${g.elevatedLabel}</label>
                </div>`;
              }
              return `<div class="role-group" data-viewer-role="${g.viewerRole}">
                <label class="role-group__viewer"><input type="checkbox" name="invite-mc" value="${g.viewerRole}"> ${g.label}</label>
                ${elevated}
              </div>`;
            }).join('');
            mcChecks = adminCheck + groupChecks;
          }

          let formGroups: string;
          if (isLimitedRole) {
            // mc-admin / editors can only invite as MC users (no dashboard role)
            formGroups = `<div class="invite-inline__group">
              <span class="invite-inline__label">MC</span>${mcChecks}
            </div>`;
          } else {
            const roleChecks = ROLES.map(
              (r) =>
                `<label><input type="checkbox" name="invite-role" value="${r.value}" ${r.value === 'employee' ? 'checked' : ''}> ${r.label}</label>`
            ).join('');
            formGroups = `<div class="invite-inline__group">
              <span class="invite-inline__label">Role</span>${roleChecks}
            </div>
            <div class="invite-inline__group">
              <span class="invite-inline__label">MC</span>${mcChecks}
            </div>`;
          }

          const inviteColspan = isLimitedRole ? 4 : 5;
          tr.innerHTML = `<td>${avatarHtml}</td>
            <td>${nameHtml}</td>
            <td>${emailHtml}</td>
            <td colspan="${inviteColspan}"><div class="invite-inline">
            ${formGroups}
            <div class="invite-inline__actions">
              <button class="btn-confirm" data-email="${escapeHtml(email)}">Send</button>
              <button class="btn-cancel">Cancel</button>
            </div>
          </div></td>`;

          // Mutual exclusion: only one dashboard role; grouped MC role logic
          tr.addEventListener('change', (ev) => {
            const cb = ev.target as HTMLInputElement;
            if (cb.name === 'invite-role' && cb.checked) {
              tr.querySelectorAll<HTMLInputElement>('input[name="invite-role"]').forEach(
                (other) => {
                  if (other !== cb) other.checked = false;
                }
              );
            }
            if (cb.name === 'invite-mc') {
              const roleGroup = cb.closest('.role-group');
              const isViewerToggle = roleGroup?.querySelector('.role-group__viewer input') === cb;

              if (isViewerToggle) {
                const elevatedDiv =
                  roleGroup?.querySelector<HTMLDivElement>('.role-group__elevated');
                const elevatedCb =
                  elevatedDiv?.querySelector<HTMLInputElement>('input[type="checkbox"]');
                if (cb.checked) {
                  if (elevatedDiv) elevatedDiv.style.display = '';
                } else {
                  if (elevatedDiv) elevatedDiv.style.display = 'none';
                  if (elevatedCb) elevatedCb.checked = false;
                }
              }
            }
          });
        }
        return;
      }

      if (target.classList.contains('btn-confirm')) {
        if (target.classList.contains('loading')) return;
        const email = target.dataset.email;
        const inviteRow = target.closest('tr');
        const checkedRole = inviteRow?.querySelector<HTMLInputElement>(
          'input[name="invite-role"]:checked'
        );
        const isExternal = !!inviteRow?.closest('#external-members-table-body');
        const role = checkedRole
          ? (checkedRole.value as UserRole)
          : isExternal
            ? ('external' as UserRole)
            : null;
        const mcRoles = Array.from(
          inviteRow?.querySelectorAll<HTMLInputElement>('input[name="invite-mc"]:checked') ?? []
        ).map((cb) => cb.value);

        if (!role && mcRoles.length === 0) {
          showMessage('error', 'Select at least one role');
          return;
        }

        if (email) {
          const btn = target as HTMLButtonElement;
          btn.textContent = 'Sending…';
          btn.classList.add('loading');
          btn.disabled = true;
          void handleInvite(email, role, mcRoles.length > 0 ? mcRoles : undefined);
        }
        return;
      }

      if (target.classList.contains('btn-cancel')) {
        // Re-render to reset the row
        void loadMembers(true);
        return;
      }

      if (target.classList.contains('btn-revoke')) {
        const invitationId = target.dataset.invitationId;
        if (invitationId) {
          void handleRevoke(invitationId);
        }
        return;
      }

      if (target.classList.contains('btn-remove')) {
        const userId = target.dataset.userId;
        if (userId) {
          void handleRemoveAccess(userId);
        }
      }
    };
    tbody?.addEventListener('click', handleClick);

    // Wire up external table delegation
    externalTbody?.addEventListener('change', handleChange);
    externalTbody?.addEventListener('click', handleClick);

    // ─── External invite form ──────────────────────
    const externalBtn = container.querySelector<HTMLButtonElement>('#invite-external-btn');
    const externalForm = container.querySelector<HTMLDivElement>('#invite-external-form');
    const handleExternalToggle = (): void => {
      if (externalForm) {
        externalForm.style.display = externalForm.style.display === 'none' ? '' : 'none';
      }
    };
    externalBtn?.addEventListener('click', handleExternalToggle);

    const externalSend = container.querySelector<HTMLButtonElement>('#external-invite-send');
    const externalRolesContainer = container.querySelector<HTMLDivElement>('#external-mc-roles');

    // Editors: hide role groups they can't manage in external invite form
    if (isEditorOnly && externalRolesContainer) {
      externalRolesContainer.querySelectorAll<HTMLDivElement>('.role-group').forEach((group) => {
        const viewerRole = group.getAttribute('data-viewer-role') ?? '';
        if (!editorViewerRoles.includes(viewerRole)) {
          group.style.display = 'none';
        }
      });
    }

    // Enable/disable Send button and handle grouped toggle logic
    const updateExternalSendState = (): void => {
      if (!externalSend || !externalRolesContainer) return;
      const anyChecked = externalRolesContainer.querySelector<HTMLInputElement>(
        'input[type="checkbox"]:checked'
      );
      externalSend.disabled = !anyChecked;
    };
    const handleExternalRoleChange = (): void => {
      updateExternalSendState();
    };
    externalRolesContainer?.addEventListener('change', handleExternalRoleChange);

    const handleExternalSend = (): void => {
      const emailInput = container.querySelector<HTMLInputElement>('#external-email');
      const email = emailInput?.value?.trim();
      const mcRoles = Array.from(
        externalRolesContainer?.querySelectorAll<HTMLInputElement>(
          'input[type="checkbox"]:checked'
        ) ?? []
      ).map((cb) => cb.value);
      if (email && mcRoles.length > 0) {
        void handleInvite(email, 'external' as UserRole, mcRoles);
        if (externalForm) externalForm.style.display = 'none';
        if (emailInput) emailInput.value = '';
        // Reset checkboxes and disable Send for next use
        externalRolesContainer
          ?.querySelectorAll<HTMLInputElement>('input[type="checkbox"]')
          .forEach((cb) => {
            cb.checked = false;
          });
        updateExternalSendState();
      } else if (email) {
        showMessage('error', 'Select at least one access role');
      }
    };
    externalSend?.addEventListener('click', handleExternalSend);

    const externalCancel = container.querySelector<HTMLButtonElement>('#external-invite-cancel');
    const handleExternalCancel = (): void => {
      if (externalForm) externalForm.style.display = 'none';
      const emailInput = container.querySelector<HTMLInputElement>('#external-email');
      if (emailInput) emailInput.value = '';
      // Reset checkboxes and Send button
      externalRolesContainer
        ?.querySelectorAll<HTMLInputElement>('input[type="checkbox"]')
        .forEach((cb) => {
          cb.checked = false;
        });
      updateExternalSendState();
    };
    externalCancel?.addEventListener('click', handleExternalCancel);

    // Back link
    const backLink = container.querySelector<HTMLAnchorElement>('#back-link');
    const handleBackClick = (e: Event): void => {
      e.preventDefault();
      navigate('/');
    };
    backLink?.addEventListener('click', handleBackClick);

    // ─── Activity Log Modal ────────────────────────
    const activityLogBtn = container.querySelector<HTMLButtonElement>('#activity-log-btn');
    const modalOverlay = document.getElementById('activity-modal-overlay');
    const modalClose = document.getElementById('activity-modal-close');
    const searchInput = document.getElementById('activity-log-search') as HTMLInputElement | null;
    const prevBtn = document.getElementById('activity-log-prev-btn') as HTMLButtonElement | null;
    const nextBtn = document.getElementById('activity-log-next-btn') as HTMLButtonElement | null;

    // Show button only for admins
    if (activityLogBtn) {
      activityLogBtn.style.display = isAdmin ? '' : 'none';
    }

    const openModal = (): void => {
      if (modalOverlay) modalOverlay.style.display = 'flex';
      void loadAuditLogs(1);
    };

    const closeModal = (): void => {
      if (modalOverlay) modalOverlay.style.display = 'none';
    };

    const handleOverlayClick = (e: Event): void => {
      if (e.target === modalOverlay) closeModal();
    };

    const handleSearchInput = (): void => {
      const query = searchInput?.value.toLowerCase().trim() ?? '';
      const rows = document.querySelectorAll<HTMLElement>('#activity-log-table-body tr');
      rows.forEach((row) => {
        const text = row.dataset.search ?? '';
        row.style.display = !query || text.includes(query) ? '' : 'none';
      });
    };

    const handlePrevPage = (): void => {
      void loadAuditLogs(auditPage - 1);
    };
    const handleNextPage = (): void => {
      void loadAuditLogs(auditPage + 1);
    };

    activityLogBtn?.addEventListener('click', openModal);
    modalClose?.addEventListener('click', closeModal);
    modalOverlay?.addEventListener('click', handleOverlayClick);
    searchInput?.addEventListener('input', handleSearchInput);
    prevBtn?.addEventListener('click', handlePrevPage);
    nextBtn?.addEventListener('click', handleNextPage);

    return () => {
      tbody?.removeEventListener('change', handleChange);
      tbody?.removeEventListener('click', handleClick);
      externalTbody?.removeEventListener('change', handleChange);
      externalTbody?.removeEventListener('click', handleClick);
      externalBtn?.removeEventListener('click', handleExternalToggle);
      externalSend?.removeEventListener('click', handleExternalSend);
      externalRolesContainer?.removeEventListener('change', handleExternalRoleChange);
      externalCancel?.removeEventListener('click', handleExternalCancel);
      backLink?.removeEventListener('click', handleBackClick);
      activityLogBtn?.removeEventListener('click', openModal);
      modalClose?.removeEventListener('click', closeModal);
      modalOverlay?.removeEventListener('click', handleOverlayClick);
      searchInput?.removeEventListener('input', handleSearchInput);
      prevBtn?.removeEventListener('click', handlePrevPage);
      nextBtn?.removeEventListener('click', handleNextPage);
    };
  }, [
    handleRoleChange,
    handleMcRoleChange,
    handleInvite,
    handleRevoke,
    handleRemoveAccess,
    loadMembers,
    loadAuditLogs,
    navigate,
    isAdmin,
    isMcAdminOnly,
    isEditorOnly,
    isLimitedRole,
    editorViewerRoles,
    showMessage,
    auditPage,
  ]);

  return <div ref={containerRef} dangerouslySetInnerHTML={{ __html: membersTemplate }} />;
}

// ─── Utility Functions ─────────────────────────────────

function escapeHtml(text: string): string {
  const div = document.createElement('div');
  div.textContent = text;
  return div.innerHTML;
}

function getInitials(firstName: string, lastName: string): string {
  return ((firstName?.[0] ?? '') + (lastName?.[0] ?? '')).toUpperCase() || '?';
}

function getAccessBadge(m: WorkspaceMember): {
  badgeClass: string;
  badgeText: string;
} {
  if (m.suspended) {
    return { badgeClass: 'suspended', badgeText: 'Suspended' };
  }
  if (m.dashboardUserId && m.dashboardStatus === 'active') {
    return { badgeClass: 'active', badgeText: 'Active' };
  }
  if (m.dashboardUserId && m.dashboardStatus === 'inactive') {
    return { badgeClass: 'inactive', badgeText: 'Inactive' };
  }
  if (m.pendingInvitationId) {
    return { badgeClass: 'pending', badgeText: 'Pending Invite' };
  }
  return { badgeClass: 'no-access', badgeText: 'No Access' };
}
